import { DataType } from '@revelio/core';

import {
  DataProviderQueryStatus,
  type DataProviderSet,
} from '../components/DataProvider/types';
import { useTabContentConfig } from '../components/PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../components/PageConfigProvider/types/chart';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import { usePrimaryFilterState } from '../filters/hooks/usePrimaryFilterState';

/**
 * Adaptive rules describe how a base chart type swaps to a different chart
 * type depending on how many entities are selected in the primary filter.
 * `shouldSwap` returns true when the adaptive target should render instead of
 * the base chart.
 */
type AdaptiveRule = {
  target: ChartType;
  shouldSwap: (entityCount: number) => boolean;
};

const ADAPTIVE_RULES: Partial<Record<ChartType, AdaptiveRule>> = {
  bar: { target: 'metric', shouldSwap: (n) => n === 1 },
  'stacked-bar': { target: 'treemap', shouldSwap: (n) => n === 1 },
  sankey: { target: 'chord', shouldSwap: (n) => n >= 3 },
};

export const ADAPTIVE_MAP: Partial<Record<ChartType, ChartType>> =
  Object.fromEntries(
    (Object.entries(ADAPTIVE_RULES) as [ChartType, AdaptiveRule][]).map(
      ([base, rule]) => [base, rule.target]
    )
  );

type UseAdaptiveChartTypeResult = {
  chartType: ChartType | undefined;
  activePrimaryFilterLength: number;
  isAdaptiveChartMode: boolean;
};

const isAdaptive = (plot: PositionedItem['plot']): boolean =>
  Boolean(plot?.adaptive && plot.chartType && ADAPTIVE_RULES[plot.chartType]);

export const getAdaptiveChartType = (
  plot: PositionedItem['plot'],
  entityCount: number
): ChartType | undefined => {
  const rule = plot?.chartType ? ADAPTIVE_RULES[plot.chartType] : undefined;
  return isAdaptive(plot) && rule?.shouldSwap(entityCount)
    ? rule.target
    : plot?.chartType;
};

export const useAdaptiveChartType = (
  item: PositionedItem | null
): UseAdaptiveChartTypeResult => {
  const { primaryFilter } = useTabContentConfig();
  const { activePrimaryFilterLength } = usePrimaryFilterState(
    primaryFilter?.id
  );
  return {
    chartType: getAdaptiveChartType(item?.plot, activePrimaryFilterLength),
    activePrimaryFilterLength,
    isAdaptiveChartMode: isAdaptive(item?.plot),
  };
};

const getEntityCountForAdaptiveDataSync = (
  data: DataProviderSet
): number | undefined => {
  if (
    data.type === DataType.SINGLE ||
    data.type === DataType.BREAKDOWN_SINGLE
  ) {
    return data.data.length;
  }
  return undefined;
};

export const shouldMaskAdaptiveEntityTransition = ({
  isAdaptiveChartMode,
  baseChartType,
  activePrimaryFilterLength,
  data,
  queryStatus = DataProviderQueryStatus.Success,
}: {
  isAdaptiveChartMode: boolean;
  baseChartType: ChartType;
  activePrimaryFilterLength: number;
  data: DataProviderSet;
  queryStatus?: DataProviderQueryStatus;
}): boolean => {
  if (queryStatus !== DataProviderQueryStatus.Fetching) {
    return false;
  }
  if (!isAdaptiveChartMode || activePrimaryFilterLength < 1) {
    return false;
  }
  if (!ADAPTIVE_MAP[baseChartType]) {
    return false;
  }
  const entityCount = getEntityCountForAdaptiveDataSync(data);
  if (entityCount === undefined || entityCount < 1) {
    return false;
  }
  return entityCount !== activePrimaryFilterLength;
};
